import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../api/axios";
import "./InExpertiseApplicationsZam2.css";
import SignatureApplicationModal from "../pages/modals/SignatureApplicationModal";
import ApplicationReviewModal from "./modals/ApplicationReviewModal";
import ChatBox from "./modals/ChatBox";

const EXP_KEY = "zam2_expanded_groups";
const FILTERS_KEY = "zam2_filters";

export default function InExpertiseApplicationsZam2({ locale }) {
  const token = localStorage.getItem("token");

  /* ========== i18n ========== */
  const t = {
    kz: {
      applications: "Сараптамадағы Өтінімдер",
      loading: "Жүктелуде...",
      view: "Қол қою",
      viewNew: "Көру",
      close: "Жабу",
      average: "Жалпы орташа балл",
      decision: "Қорытынды",
      download: "Қорытындыны жүктеу",
      signatures: "Қол қою тізімі",
      accept: "Қабылдау",
      reject: "Қабылдамау",
      searchPlaceholder: "Топ атауы бойынша іздеу…",
      filters: "Фильтрлер",
      subjects: "Пәндер",
      clear: "Тазарту",
      expandAll: "Барлығын ашу",
      collapseAll: "Барлығын жабу",
      noGroups: "Сәйкес топтар табылмады",
      columns: {
        publisher: "Баспа",
        title: "Оқу басылымы",
        subject: "Пән",
        grade: "Сынып",
        lang: "Әзірлеу тілі",
        category: "Санат",
        action: "Әрекет",
      },
      alerts: {
        errorOccurred: "Қате орын алды",
        fileNotLoaded: "❗ Құжат жүктелмеді",
        signError: "❌ Қате: ",
        signSuccess: "✅ Қол қойылды!",
        rejectQueueError: "Қате: қол қою кезегінде жоқсыз",
        rejectSuccess: "❌ Қабылдамадыңыз",
        generalRejectError: "Қате",
        fileDownloadError: "Файлды жүктеу қатесі",
      },
      newModalTitle: "Жаңа модал",
      opening: "Ашу...",
    },
    ru: {
      applications: "Заявки на экспертизе",
      loading: "Загрузка...",
      view: "Подписать",
      viewNew: "Просмотр",
      close: "Закрыть",
      average: "Общий средний балл",
      decision: "Заключение",
      download: "Скачать итог",
      signatures: "Подписи",
      accept: "Подписать",
      reject: "Отклонить",
      searchPlaceholder: "Поиск по названию группы…",
      filters: "Фильтры",
      subjects: "Предметы",
      clear: "Сбросить",
      expandAll: "Открыть всё",
      collapseAll: "Закрыть всё",
      noGroups: "Подходящих групп не найдено",
      columns: {
        publisher: "Издательство",
        title: "Учебное издание",
        subject: "Предмет",
        grade: "Класс",
        lang: "Язык разработки",
        category: "Категория",
        action: "Действие",
      },
      alerts: {
        errorOccurred: "Произошла ошибка",
        fileNotLoaded: "❗ Файл не загружен",
        signError: "❌ Ошибка: ",
        signSuccess: "✅ Подпись успешна!",
        rejectQueueError: "Ошибка: вы не в очереди на подпись",
        rejectSuccess: "❌ Вы отклонили",
        generalRejectError: "Ошибка",
        fileDownloadError: "Ошибка при загрузке файла",
      },
      newModalTitle: "Новая модалка",
      opening: "Открываю...",
    },
  }[locale];

  /* ========== state ========== */
  const [applicationGroups, setApplicationGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // accordion
  const [expanded, setExpanded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(EXP_KEY) || "{}");
    } catch {
      return {};
    }
  });
  useEffect(() => {
    localStorage.setItem(EXP_KEY, JSON.stringify(expanded));
  }, [expanded]);

  // filters (sync with URL & LS)
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || "{}");
      return params.get("q") ?? saved.q ?? "";
    } catch {
      return params.get("q") ?? "";
    }
  });
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState(() => {
    try {
      const fromURL = params.get("s");
      if (fromURL) return fromURL.split(",").filter(Boolean);
      const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || "{}");
      return saved.subjects || [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    q ? p.set("q", q) : p.delete("q");
    selectedSubjects.length
      ? p.set("s", selectedSubjects.join(","))
      : p.delete("s");
    const newUrl = `${window.location.pathname}?${p.toString()}`;
    window.history.replaceState(null, "", newUrl);
    localStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({ q, subjects: selectedSubjects })
    );
  }, [q, selectedSubjects]);

  // pagination
  const groupsPerPage = 4;
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => setCurrentPage(1), [q, selectedSubjects]);

  // Signature modal (old flow)
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [finalAverage, setFinalAverage] = useState(null);
  const [summaryDecision, setSummaryDecision] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [userId, setUserId] = useState(null);
  const [showSignatures, setShowSignatures] = useState(false);
  const [base64File, setBase64File] = useState("");

  // Review modal (new flow)
  const [reviewApp, setReviewApp] = useState(null);
  const [reviewFinalAverage, setReviewFinalAverage] = useState(null);
  const [reviewSummaryDecision, setReviewSummaryDecision] = useState("");
  const [reviewExperts, setReviewExperts] = useState([]);
  const [reviewFinalDocuments, setReviewFinalDocuments] = useState([]);

  // Chat open flag (for ChatBox inside ApplicationReviewModal)
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => setChatOpen(true);
  const closeChat = () => setChatOpen(false);

  // Loading flags for buttons
  const [openingSign, setOpeningSign] = useState(false);
  const [openingReview, setOpeningReview] = useState(false);

  /* ========== effects ========== */
  useEffect(() => {
    fetchUserProfile();
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUserProfile() {
    try {
      const res = await axios.get("/internal/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserId(res.data?.id ?? null);
    } catch (err) {
     
    }
  }

  async function fetchApplications() {
    setLoading(true);
    try {
      const res = await axios.get("/expertise/deputy2/in-expertise-applications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplicationGroups(res.data?.applications || []);
    } catch (err) {
     
    } finally {
      setLoading(false);
    }
  }

  /* ========== helpers ========== */
  const gid = (g, idx) => String(g.group_id ?? g.id ?? idx);

  const isOpen = (g, idx) => {
    const id = gid(g, idx);
    if (expanded[id] === undefined) return idx === 0;
    return !!expanded[id];
  };

  const toggleGroup = (g, idx) => {
    const id = gid(g, idx);
    setExpanded((prev) => ({ ...prev, [id]: !isOpen(g, idx) }));
  };

  const allSubjects = useMemo(() => {
    const set = new Set();
    (applicationGroups || []).forEach((grp) =>
      (grp.applications || []).forEach((a) => a.subject && set.add(a.subject))
    );
    return Array.from(set).sort();
  }, [applicationGroups]);

  const norm = (s) => String(s ?? "").toLowerCase().trim();

  const filteredGroups = useMemo(() => {
    const qn = norm(q);
    return (applicationGroups || [])
      .map((g) => {
        const apps = (g.applications || []).filter((a) => {
          const subjOk = selectedSubjects.length
            ? selectedSubjects.includes(a.subject)
            : true;
          return subjOk;
        });
        const titleOk = !qn || norm(g.group_title).includes(qn);
        if (titleOk && apps.length) return { ...g, applications: apps };
        return null;
      })
      .filter(Boolean);
  }, [applicationGroups, q, selectedSubjects]);

  const totalPages =
    Math.ceil((filteredGroups.length || 0) / groupsPerPage) || 1;
  const pageSliceStart = (currentPage - 1) * groupsPerPage;
  const pageSliceEnd = pageSliceStart + groupsPerPage;
  const paginatedGroups = filteredGroups.slice(pageSliceStart, pageSliceEnd);

  const allOpenOnPage = useMemo(() => {
    return (paginatedGroups || []).every((g, i) => {
      const id = gid(g, i + pageSliceStart);
      const v = expanded[id];
      return v === undefined ? i === 0 && currentPage === 1 : !!v;
    });
  }, [paginatedGroups, expanded, currentPage, pageSliceStart]);

  const toggleAll = () => {
    const next = { ...expanded };
    paginatedGroups.forEach((g, i) => {
      next[gid(g, i + pageSliceStart)] = !allOpenOnPage;
    });
    setExpanded(next);
  };

  /* ========== data loaders for modals ========== */

  // /expertise/internal/applications/:id/external-progress
  const loadExternalProgress = useCallback(
    async (applicationId) => {
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `/expertise/internal/applications/${applicationId}/external-progress`,
        { headers }
      );

      return {
        experts: Array.isArray(data?.experts) ? data.experts : [],
        finalAverage: data?.final_average ?? null,
        summaryDecision: data?.summary_decision ?? "",
      };
    },
    [token]
  );

  /* ========== open modals ========== */

  // Старая — подпись/отклонение
  async function openApplication(app) {
    try {
      setOpeningSign(true);

      const headers = { Authorization: `Bearer ${token}` };

      const docRes = await axios.get(
        `/final-documents/by-application/${app.id}`,
        { headers }
      );

      const statusRes = await axios.get(
        `/final-documents/${docRes.data.document_id}/signatures/status`,
        { headers }
      );

      const pdfRes = await axios.get(
        `/final-documents/${docRes.data.document_id}/base64`,
        { headers }
      );

      setSelectedApplication({
        ...app,
        document_id: docRes.data.document_id,
        title: docRes.data.application_data.title,
        category: docRes.data.application_data.category,
        authors: docRes.data.application_data.authors,
        publisher: docRes.data.application_data.publisher,
        grade: docRes.data.application_data.grade,
      });
      setFinalAverage(docRes.data.average_score);
      setSummaryDecision(docRes.data.verdict);
      setSignatures(docRes.data.signers || []);
      setPendingSignatures(statusRes.data.not_signed || []);
      setBase64File(pdfRes.data.base64);
    } catch (err) {
   
      alert(t.alerts.errorOccurred);
    } finally {
      setOpeningSign(false);
    }
  }

  // Новая — детальный просмотр
  async function openApplicationReview(app) {
    try {
      setOpeningReview(true);

      const headers = { Authorization: `Bearer ${token}` };
      // грузим данные по финал-докам и прогрессу внешних экспертов параллельно
      const [{ data }, ext] = await Promise.all([
        axios.get(`/expertise/internal/final-documents/${app.id}`, { headers }),
        loadExternalProgress(app.id),
      ]);

      const docs = Array.isArray(data?.documents) ? data.documents : [];
      const active =
        docs.find((d) => d?.is_active) ||
        [...docs].sort(
          (a, b) =>
            new Date(b?.updated_at || b?.created_at || 0) -
            new Date(a?.updated_at || a?.created_at || 0)
        )[0] ||
        null;

      setReviewApp({ ...app });
      setReviewFinalDocuments(docs);

      setReviewExperts(
        (ext.experts || []).map((e) => ({
          id: e.id,
          name: e.name,
          subject: e.subject || "—",
          status: e.status || "—",
          score: e.score ?? "—",
          has_mistakes_file: !!e.has_mistakes_file,
          has_decision_file: !!e.has_decision_file,
        }))
      );

      setReviewFinalAverage(ext.finalAverage ?? active?.average_score ?? null);
      setReviewSummaryDecision(ext.summaryDecision || active?.verdict || "");
    } catch (err) {
    
      alert(t.alerts.errorOccurred);
    } finally {
      setOpeningReview(false);
    }
  }

  /* ========== signing actions ========== */
  async function handleSign() {
    if (!base64File) return alert(t.alerts.fileNotLoaded);

    window.blockScreen?.();
    window.createCAdESFromBase64(
      "PKCS12",
      "SIGNATURE",
      base64File,
      false,
      async function (result) {
        window.unblockScreen?.();
        if (result.code !== "200") {
          alert(t.alerts.signError + result.message);
          return;
        }
        try {
          await axios.post(
            `/final-documents/sign/${selectedApplication.document_id}`,
            {
              signed_pdf_base64: result.responseObject,
              filename: `signed_doc_${selectedApplication.document_id}_${userId}.pdf`,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          alert(t.alerts.signSuccess);
          setSelectedApplication(null);
          fetchApplications();
        } catch (err) {
          alert(err.response?.data?.message || t.alerts.errorOccurred);
        }
      }
    );
  }

  async function handleReject() {
    try {
      const currentSig = pendingSignatures.find((sig) => sig.user_id === userId);
      if (!currentSig) return alert(t.alerts.rejectQueueError);

      await axios.post(`/final-documents/reject/${currentSig.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(t.alerts.rejectSuccess);
      setSelectedApplication(null);
      fetchApplications();
    } catch (err) {
      alert(err.response?.data?.message || t.alerts.generalRejectError);
    }
  }

  async function downloadFinalDecision(finalDocId) {
  return downloadFinalPdf(finalDocId);
}

  async function downloadFinalPdf(finalDocId) {
  try {
    const res = await axios.get(`/final-documents/download-pdf/${finalDocId}`, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    });

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `final_document_${finalDocId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
   
    alert(t.alerts.fileDownloadError);
  }
}

  /* ===== ЗАГРУЗКА ФАЙЛОВ ВНЕШНИХ ЭКСПЕРТОВ ===== */

async function downloadMistakesFile(applicationId, expertId) {
  try {
    const res = await axios.get(
      `/expertise/internal/applications/${applicationId}/external-experts/${expertId}/mistakes/download`,
      { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
    );
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `mistakes_${expertId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
   
    alert(t.alerts.fileDownloadError);
  }
}

async function downloadDecisionFile(applicationId, expertId) {
  try {
    const res = await axios.get(
      `/expertise/internal/applications/${applicationId}/external-experts/${expertId}/decision/download`,
      { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
    );
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `decision_${expertId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
   
    alert(t.alerts.fileDownloadError);
  }
}


  /* ========== render ========== */
  const trExpertStatus = useCallback((s) => s || "—", []);
  const trVerdict = useCallback((v) => v || "—", []);

  if (loading) return <p className="iealLoading">{t.loading}</p>;
// стабильность пропов

  if (!filteredGroups.length) {
    return (
      <div className="iealContainer">
        <Toolbar
          q={q}
          setQ={setQ}
          subjectsOpen={subjectsOpen}
          setSubjectsOpen={setSubjectsOpen}
          allSubjects={allSubjects}
          selectedSubjects={selectedSubjects}
          setSelectedSubjects={setSelectedSubjects}
          toggleAll={toggleAll}
          allOpenOnPage={allOpenOnPage}
          t={t}
        />
        <p className="iealEmpty">{t.noGroups}</p>
      </div>
    );
  }

  // компактная пагинация: boundaryCount — сколько страниц показывать у краёв,
// siblingCount — сколько страниц вокруг текущей
function buildCompactPages(total, current, { boundaryCount = 1, siblingCount = 1 } = {}) {
  const E = "…";
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const start = Math.max(2, current - siblingCount);
  const end = Math.min(total - 1, current + siblingCount);

  const pages = [1];

  // левый блок
  if (start > boundaryCount + 2) {
    pages.push(E);
  } else {
    for (let i = 2; i < start; i++) pages.push(i);
  }

  // середина (вокруг текущей)
  for (let i = start; i <= end; i++) pages.push(i);

  // правый блок
  if (end < total - (boundaryCount + 1)) {
    pages.push(E);
  } else {
    for (let i = end + 1; i <= total - 1; i++) pages.push(i);
  }

  pages.push(total);
  // удалим возможные дубли
  return pages.filter((v, idx, arr) => idx === 0 || arr[idx - 1] !== v);
}


  return (
    <div className="iealContainer">
      <Toolbar
        q={q}
        setQ={setQ}
        subjectsOpen={subjectsOpen}
        setSubjectsOpen={setSubjectsOpen}
        allSubjects={allSubjects}
        selectedSubjects={selectedSubjects}
        setSelectedSubjects={setSelectedSubjects}
        toggleAll={toggleAll}
        allOpenOnPage={allOpenOnPage}
        t={t}
      />

      {paginatedGroups.map((group, gIdx) => {
        const globalIdx = pageSliceStart + gIdx;
        const open = isOpen(group, globalIdx);
        const appsCount = group.applications?.length || 0;

        return (
          <div key={gid(group, globalIdx)} className="iealGroup">
            <button
              type="button"
              className="iealAccHeader"
              onClick={() => toggleGroup(group, globalIdx)}
              aria-expanded={open}
            >
              <span className="iealChev">{open ? "▾" : "▸"}</span>
              <span className="iealGTitle">{group.group_title}</span>
              <span className="iealBadge">{appsCount}</span>
            </button>

            {open && (
              <div className="iealAccPanel">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>{t.columns.publisher}</th>
                      <th>{t.columns.title}</th>
                      <th>{t.columns.subject}</th>
                      <th>{t.columns.grade}</th>
                      <th>{t.columns.lang}</th>
                      <th>{t.columns.category}</th>
                      <th>{t.columns.action}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(group.applications || []).map((app) => (
                      <tr key={app.id}>
                        <td>{app.publisher}</td>
                        <td>{app.title}</td>
                        <td>{app.subject}</td>
                        <td>{app.grade}</td>
                        <td>{app.development_language}</td>
                        <td>{app.category}</td>
                        <td>
                          <button
                            className="btn-primary"
                            onClick={() => openApplication(app)}
                            disabled={openingSign}
                            title={openingSign ? t.opening : t.view}
                          >
                            {openingSign ? "…" : t.view}
                          </button>

                          <button
                            className="btn-secondary"
                            style={{ marginLeft: 8 }}
                            onClick={() => openApplicationReview(app)}
                            disabled={openingReview}
                            title={t.newModalTitle}
                          >
                            {openingReview ? "…" : t.viewNew}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Старая модалка подписи */}
      {selectedApplication && (
        <SignatureApplicationModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          locale={locale}
          finalAverage={finalAverage}
          summaryDecision={summaryDecision}
          t={t}
          downloadFinalDecision={() => downloadFinalPdf(selectedApplication.document_id)}
          showSignatures={showSignatures}
          setShowSignatures={setShowSignatures}
          signatures={signatures}
          userId={userId}
          pendingSignatures={pendingSignatures}
          handleSign={handleSign}
          handleReject={handleReject}
        />
      )}

      {/* Новая модалка детального просмотра */}
      {reviewApp && (
  <ApplicationReviewModal
  isOpen
  application={reviewApp}
  experts={reviewExperts}
  locale={locale}
  t={t}
  finalAverage={reviewFinalAverage}
  summaryDecision={reviewSummaryDecision}
  trExpertStatus={trExpertStatus}
  trVerdict={trVerdict}
  onClose={() => { setReviewApp(null); closeChat(); }}
  downloadFinalDecision={(finalDocId) => downloadFinalPdf(finalDocId)}
  finalDocuments={reviewFinalDocuments}

  downloadMistakesFile={downloadMistakesFile}
  downloadDecisionFile={downloadDecisionFile}

    onOpenChat={openChat}
    ChatBoxComponent={ChatBox}
chatBoxProps={{
  key: `chat-${reviewApp.id}-${chatOpen}`, // форс-ремоунт при открытии
  isOpen: chatOpen,
  onClose: closeChat,
  applicationId: reviewApp.id,

  // ВАЖНО: правильные имена
  currentUserId: userId,
  lang: (locale === 'kz' ? 'kk' : locale), // нормализация kz -> kk

  token,
}}

  />
)}


      {totalPages > 1 && (
  <div className="iealPagination">
    <button
      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
      disabled={currentPage === 1}
      aria-label="Previous page"
    >
      ←
    </button>

    {buildCompactPages(totalPages, currentPage, { boundaryCount: 1, siblingCount: 1 })
      .map((item, idx) => {
        if (item === "…") {
          return (
            <span key={`dots-${idx}`} className="iealDots" aria-hidden>
              …
            </span>
          );
        }
        const pageNum = Number(item);
        return (
          <button
            key={pageNum}
            onClick={() => setCurrentPage(pageNum)}
            className={currentPage === pageNum ? "is-active" : ""}
            aria-current={currentPage === pageNum ? "page" : undefined}
          >
            {pageNum}
          </button>
        );
      })}

    <button
      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
      disabled={currentPage === totalPages}
      aria-label="Next page"
    >
      →
    </button>
  </div>
)}

    </div>
  );
}

/* ===== Toolbar subcomponent ===== */
function Toolbar({
  q,
  setQ,
  subjectsOpen,
  setSubjectsOpen,
  allSubjects,
  selectedSubjects,
  setSelectedSubjects,
  toggleAll,
  allOpenOnPage,
  t,
}) {
  return (
    <div className="iealToolbar">
      <input
        className="iealSearch"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t.searchPlaceholder}
      />

      <div className="iealFilters">
        <button
          className="iealAButton"
          onClick={() => setSubjectsOpen((v) => !v)}
        >
          {t.filters}
          {selectedSubjects.length ? ` (${selectedSubjects.length})` : ""}
        </button>

        {subjectsOpen && (
          <div className="iealFilterPanel">
            <div className="iealFilterHeader">
              <span>{t.subjects}</span>
              <button
                className="iealClearBtn"
                onClick={() => setSelectedSubjects([])}
              >
                {t.clear}
              </button>
            </div>

            <div className="iealChips">
              {allSubjects.map((s) => {
                const active = selectedSubjects.includes(s);
                return (
                  <button
                    key={s}
                    className={`iealChip ${active ? "is-active" : ""}`}
                    onClick={() =>
                      setSelectedSubjects((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s]
                      )
                    }
                    title={s}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <button className="iealAButton" onClick={toggleAll}>
        {allOpenOnPage ? t.collapseAll : t.expandAll}
      </button>
    </div>
  );
}
